// Lightweight, dependency-free GraphQL client for the admin SPA.
//
// We deliberately avoid Apollo/urql/relay: the admin plane consumes a small, hand-written
// set of operations (typed against the generated ./types.ts), so a single `fetch`-based
// transport plus a tiny hook layer (hooks.ts) is all we need. This keeps the bundle small
// (DESIGN_LANGUAGE / web performance budget) and the data flow easy to reason about.
//
// The transport POSTs `{ query, variables }` to `/api/graphql`, attaches the operator's
// Bearer JWT when present, and surfaces both HTTP and GraphQL-level errors as typed throws
// so callers (and the hooks) have a single failure channel.

/** Endpoint for the Absinthe GraphQL plane. Relative so it works behind any host/proxy. */
export const GRAPHQL_ENDPOINT = '/api/graphql';

/** A single GraphQL error as returned in the `errors` array of a response body. */
export interface GraphQLResponseError {
  message: string;
  path?: ReadonlyArray<string | number>;
  extensions?: Record<string, unknown>;
}

/** The raw GraphQL response envelope (`{ data, errors }`). */
interface GraphQLResponseBody<TData> {
  data?: TData | null;
  errors?: GraphQLResponseError[];
}

/**
 * Error thrown for any failed GraphQL request. Carries the structured GraphQL errors (when
 * the server returned a well-formed error body) and the HTTP status (when the failure was
 * transport-level), so UI code can branch on `status === 401` to force re-auth.
 */
export class GraphQLRequestError extends Error {
  readonly graphQLErrors: GraphQLResponseError[];
  readonly status: number | undefined;

  constructor(
    message: string,
    options: {
      graphQLErrors?: GraphQLResponseError[];
      status?: number | undefined;
    } = {},
  ) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.graphQLErrors = options.graphQLErrors ?? [];
    this.status = options.status;
  }
}

export interface GraphQLRequestOptions {
  /** Operator Bearer JWT. When present, sent as `Authorization: Bearer <token>`. */
  token?: string | null | undefined;
  /** Abort signal for cancellation (the hooks pass one on unmount). */
  signal?: AbortSignal | undefined;
}

/**
 * Execute a GraphQL operation and return its typed `data`.
 *
 * Throws {@link GraphQLRequestError} on:
 *  - a non-2xx HTTP response (status preserved),
 *  - a body containing `errors`,
 *  - a body with no `data` (malformed response).
 *
 * @typeParam TData      Shape of the operation's `data` field (from ./types.ts slices).
 * @typeParam TVariables Operation variables.
 */
export async function graphqlRequest<TData, TVariables = Record<string, unknown>>(
  query: string,
  variables?: TVariables,
  options: GraphQLRequestOptions = {},
): Promise<TData> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  const fetchInit: RequestInit = {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables: variables ?? {} }),
  };
  if (options.signal) {
    fetchInit.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(GRAPHQL_ENDPOINT, fetchInit);
  } catch (cause: unknown) {
    // Network/transport failure (offline, DNS, CORS preflight, abort).
    throw new GraphQLRequestError(
      cause instanceof Error ? cause.message : 'Network request failed',
    );
  }

  let body: GraphQLResponseBody<TData> | null = null;
  try {
    body = (await response.json()) as GraphQLResponseBody<TData>;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const message =
      body?.errors?.[0]?.message ??
      `GraphQL request failed with status ${response.status}`;
    throw new GraphQLRequestError(message, {
      ...(body?.errors ? { graphQLErrors: body.errors } : {}),
      status: response.status,
    });
  }

  if (body?.errors && body.errors.length > 0) {
    throw new GraphQLRequestError(body.errors[0]?.message ?? 'GraphQL error', {
      graphQLErrors: body.errors,
      status: response.status,
    });
  }

  if (!body || body.data === undefined || body.data === null) {
    throw new GraphQLRequestError('GraphQL response contained no data', {
      status: response.status,
    });
  }

  return body.data;
}
